import { useCallback, useEffect } from "react";
import { DatePicker, Form, Input, Select, Button } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PhoneNumberInput from "components/shared-components/PhoneNumberInput";
import { buildPhoneRule } from "utils/phone";

export default function StudentForm({
  schools,
  onSubmit,
  form,
  isMinor = false,
  onValuesChange,
  isEditing = false, // Ajout du paramètre isEditing avec false par défaut
}) {
  const { t } = useTranslation();
  const { loading } = useSelector((state) => state?.students);

  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@$!%*?&";
    let password = "";
    for (let i = 0; i < 12; i += 1) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleGeneratePassword = useCallback(() => {
    const password = generateRandomPassword();
    form?.setFieldsValue({
      password,
      confirmPassword: password,
    });
  }, [form]);

  useEffect(() => {
    if (!isEditing) {
      handleGeneratePassword();
    }
  }, [isEditing, handleGeneratePassword]);

  return (
    <Form
      layout="vertical"
      name="student"
      id="student_form"
      form={form}
      onFinish={onSubmit}
      onValuesChange={onValuesChange}
      disabled={loading}
      style={{
        maxWidth: 600,
        margin: "0 auto",
      }}
    >
      <Form.Item
        label={t("students.crud.form.label.school")}
        name="schoolId"
        rules={[
          {
            required: true,
            message: t("students.crud.form.error.label.required.school"),
          },
        ]}
      >
        <Select>
          {schools?.map((school) => (
            <Select.Option key={school?._id} value={school?._id}>
              {school?.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.fullName")}
        name="fullName"
        rules={[
          {
            required: true,
            message: t("students.crud.form.error.label.required.fullName"),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="phoneNumber"
        label={t("students.crud.form.label.phoneNumber")}
        rules={[
          {
            required: true,
            message: t("students.crud.form.error.label.required.phoneNumber"),
          },
          buildPhoneRule(t, false),
        ]}
      >
        <PhoneNumberInput />
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.email")}
        name="email"
        hasFeedback
        rules={[
          {
            required: true,
            message: t("students.crud.form.error.label.required.email"),
            type: "email",
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item label={t("students.crud.form.label.address")} name="address">
        <Input />
      </Form.Item>
      <Form.Item
        className="w-full"
        label={t("students.crud.form.label.idCardNumber")}
        name="identityCardNumber"
      >
        <Input type="number" aria-controls={false} className="w-full" />
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.sex")}
        name="sex"
      >
        <Select>
          <Select.Option value="Male">
            {t("students.form.sex.options.male")}
          </Select.Option>
          <Select.Option value="Female">
            {t("students.form.sex.options.female")}
          </Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.bloodGroup")}
        name="bloodGroup"
      >
        <Select>
          <Select.Option value="O+">O+</Select.Option>
          <Select.Option value="O-">O-</Select.Option>
          <Select.Option value="A+">A+</Select.Option>
          <Select.Option value="A-">A-</Select.Option>
          <Select.Option value="B+">B+</Select.Option>
          <Select.Option value="B-">B-</Select.Option>
          <Select.Option value="AB+">AB+</Select.Option>
          <Select.Option value="AB-">AB-</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.birthDate")}
        name="birthDate"
      >
        <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.favCurrency")}
        name="favCurrency"
        initialValue="dzd"
      >
        <Select>
          <Select.Option value="dzd">DZD</Select.Option>
          <Select.Option value="eur">EUR</Select.Option>
          <Select.Option value="usd">USD</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.heardAboutUs")}
        name="heardAboutUs"
      >
        <Select>
          <Select.Option value="Réseaux sociaux">
            {t("students.form.heardAboutUs.options.socialMedia")}
          </Select.Option>
          <Select.Option value="Bouche à oreille">
            {t("students.form.heardAboutUs.options.wordOfMouth")}
          </Select.Option>
          <Select.Option value="Site web">
            {t("students.form.heardAboutUs.options.website")}
          </Select.Option>
          <Select.Option value="Publicité">
            {t("students.form.heardAboutUs.options.advertising")}
          </Select.Option>
          <Select.Option value="Autre">
            {t("students.form.heardAboutUs.options.other")}
          </Select.Option>
        </Select>
      </Form.Item>
      {/* Champs de mot de passe - affichés uniquement lors de la création */}
      {!isEditing && (
        <>
          <Form.Item
            label={t("students.crud.form.label.password")}
            name="password"
            hasFeedback
            rules={[
              {
                required: true,
                message: t("students.crud.form.error.label.required.password"),
              },
              {
                min: 6,
                message: t("students.crud.form.error.label.minLength.password"),
              },
            ]}
          >
            <Input.Password
              addonAfter={
                <Button
                  type="link"
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleGeneratePassword}
                >
                  {t("students.form.button.generatePassword", {
                    defaultValue: "Générer",
                  })}
                </Button>
              }
            />
          </Form.Item>

          <Form.Item
            label={t("students.crud.form.label.confirmPassword")}
            name="confirmPassword"
            dependencies={["password"]}
            hasFeedback
            rules={[
              {
                required: true,
                message: t(
                  "students.crud.form.error.label.required.confirmPassword"
                ),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise?.resolve();
                  }
                  return Promise?.reject(
                    t("students.crud.form.error.password.notMatch")
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </>
      )}
      {/* Informations des parents - affichées conditionnellement en fonction de l'âge */}
      <div style={{ marginTop: "20px", marginBottom: "10px" }}>
        <h4>{t("students.title.parentDetails")}</h4>
        {isMinor && (
          <div style={{ color: "#ff4d4f", marginBottom: "10px" }}>
            {t("students.crud.form.parentInfo.requiredNotice")}
          </div>
        )}
      </div>
      <Form.Item
        label={t("students.crud.form.label.parentFullName")}
        name="fatherFullName"
        rules={[
          isMinor && {
            required: true,
            message: t(
              "students.crud.form.error.label.required.parentFullName"
            ),
          },
        ]?.filter(Boolean)}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="fatherPhoneNumber"
        label={t("students.crud.form.label.parentPhoneNumber")}
        rules={[
          isMinor && {
            required: true,
            message: t(
              "students.crud.form.error.label.required.parentPhoneNumber"
            ),
          },
          buildPhoneRule(t, false),
        ]?.filter(Boolean)}
      >
        <PhoneNumberInput />
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.parentOccupation")}
        name="fatherOccupation"
        rules={[
          isMinor && {
            required: true,
            message: t(
              "students.crud.form.error.label.required.parentOccupation"
            ),
          },
        ]?.filter(Boolean)}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t("students.crud.form.label.parentIdCardNumber")}
        name="fatherNationalId"
        rules={[
          isMinor && {
            required: true,
            message: t(
              "students.crud.form.error.label.required.parentIdCardNumber"
            ),
          },
          {
            min: 4,
            message: t(
              "students.crud.form.error.label.minLength.parentIdCardNumber"
            ),
          },
        ]?.filter(Boolean)}
      >
        <Input />
      </Form.Item>
    </Form>
  );
}
